import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { leaveService } from '@/services/leave.service';
import { getTodayIST } from '@/lib/dateFormat';

/**
 * Upcoming dates HR blocked for leave (HRMS Config > Leave Config > Blocked Dates), for the
 * apply calendars. The server rejects these dates too; this only keeps them unpickable.
 */
export function useLeaveBlockedDates(enabled = true) {
  const from = getTodayIST();
  const query = useQuery({
    queryKey: ['leave', 'blocked-dates', { from }],
    queryFn: () => leaveService.listBlockedDates({ from }),
    enabled,
  });

  const blocked = useMemo(() => query.data ?? [], [query.data]);
  const keys = useMemo(() => new Set(blocked.map((b) => b.blocked_date)), [blocked]);

  const isBlocked = useCallback((d: Date) => keys.has(format(d, 'yyyy-MM-dd')), [keys]);

  /** First blocked yyyy-MM-dd within [start, end], if any (list is sorted by date). */
  const firstBlockedIn = useCallback(
    (start: string, end: string) => blocked.find((b) => b.blocked_date >= start && b.blocked_date <= end),
    [blocked],
  );

  /** First blocked date strictly after `start` — a leave range starting there must end before it. */
  const firstBlockedAfter = useCallback(
    (start: string) => blocked.find((b) => b.blocked_date > start),
    [blocked],
  );

  return { blocked, isLoading: query.isLoading, isBlocked, firstBlockedIn, firstBlockedAfter };
}
